import type { ListCustomersParams, ListCustomersResult } from '@/contracts/customers'
import type { SessionUser } from '@/types/api'

const customerListCachePrefix = 'miaoform:customer-list-page-cache:v1:'
const customerListLoginMarkerKey = 'miaoform:customer-list-page-cache:login-marker'
const customerListSessionMarkerKey = 'miaoform:customer-list-page-cache:session-marker'

type CachedCustomerListPage = {
  cachedAt: string
  params: ListCustomersParams
  result: ListCustomersResult
}

export function readCustomerListPageCache(params: ListCustomersParams) {
  const storage = getStorage()
  if (!storage) return null
  const raw = storage.getItem(createCustomerListPageCacheKey(params))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as CachedCustomerListPage
    return parsed.result
  } catch {
    return null
  }
}

export function writeCustomerListPageCache(
  params: ListCustomersParams,
  result: ListCustomersResult,
) {
  const storage = getStorage()
  if (!storage) return
  const payload: CachedCustomerListPage = {
    cachedAt: new Date().toISOString(),
    params,
    result,
  }
  storage.setItem(createCustomerListPageCacheKey(params), JSON.stringify(payload))
}

export function clearCustomerListPageCache() {
  const storage = getStorage()
  if (!storage) return
  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index)
    if (key?.startsWith(customerListCachePrefix)) storage.removeItem(key)
  }
}

export function resetCustomerListPageCacheForLogin(user: SessionUser | null | undefined) {
  const storage = getStorage()
  const sessionStorage = getSessionStorage()
  if (!storage || !user) return
  const nextMarker = `${user.userId || 'unknown'}|${user.email || 'unknown'}`
  if (sessionStorage?.getItem(customerListSessionMarkerKey) === nextMarker) return
  clearCustomerListPageCache()
  storage.setItem(customerListLoginMarkerKey, nextMarker)
  sessionStorage?.setItem(customerListSessionMarkerKey, nextMarker)
}

export function clearCustomerListPageCacheSession() {
  const storage = getStorage()
  if (!storage) return
  clearCustomerListPageCache()
  storage.removeItem(customerListLoginMarkerKey)
  getSessionStorage()?.removeItem(customerListSessionMarkerKey)
}

function createCustomerListPageCacheKey(params: ListCustomersParams) {
  const normalized = {
    keyword: params.keyword?.trim() || '',
    formId: params.formId || '',
    ownerEmail: params.ownerEmail || '',
    status: params.status || 'all',
    filingStatus: params.filingStatus || 'all',
    sort: params.sort || 'updated_desc',
    page: params.page || 1,
    limit: params.limit || 20,
  }
  return `${customerListCachePrefix}${JSON.stringify(normalized)}`
}

function getStorage() {
  if (typeof window === 'undefined') return null
  return window.localStorage
}

function getSessionStorage() {
  if (typeof window === 'undefined') return null
  return window.sessionStorage
}
